/**
 * "Did the new teammate actually open anything?"
 *
 * This is the one event that says whether landing invitees in the console
 * bought anything. The funnel it replaces could only report how many people
 * left for a download page; it had nothing to say about whether anyone read a
 * document. Reaching a document, a tracker item, or a request is the smallest
 * thing that is unambiguously *using the product* rather than looking at a
 * shell — a docs home with an empty tree is not.
 *
 * Fired at most once per browser session. `sessionStorage`, not `localStorage`:
 * the question is whether this visit went anywhere, and a permanent flag would
 * answer it once and then stay silent for every later visit.
 */

import type { ConsoleRoute } from '../routing';
import type { FirstOpenKind } from './invitationAnalyticsContract';
import { invitationFirstOpen } from '../auth/invitationHandoffEvents';

const SESSION_KEY = 'nimbalyst:web-console:first-open-reported';

/**
 * The category a route counts as, or null when reaching it proves nothing.
 *
 * Deliberately excludes the list surfaces. Docs home, the trackers list and
 * the requests inbox are all reachable by landing, so counting them would make
 * the metric a synonym for "the redirect worked".
 */
export function firstOpenKindForRoute(route: ConsoleRoute): FirstOpenKind | null {
  if (route.kind === 'document') return 'document';
  if (route.kind === 'tracker-item') return 'tracker';
  if (route.kind === 'feedbackRequest') return 'request';
  return null;
}

interface SessionFlagStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserSessionStorage(): SessionFlagStorage {
  return window.sessionStorage;
}

/**
 * Report the first meaningful open of this session, if this route is one.
 *
 * Returns whether it reported, so the caller can be tested without reaching
 * into the analytics seam.
 */
export function reportFirstOpen(
  route: ConsoleRoute,
  getStorage: () => SessionFlagStorage = browserSessionStorage,
): boolean {
  const kind = firstOpenKindForRoute(route);
  if (!kind) return false;
  try {
    const storage = getStorage();
    if (storage.getItem(SESSION_KEY)) return false;
    storage.setItem(SESSION_KEY, kind);
  } catch {
    // Unreadable storage would otherwise report on every navigation, which is
    // worse than not reporting: it would silently inflate the one number this
    // change is judged by.
    return false;
  }
  invitationFirstOpen({ kind });
  return true;
}
